import React from "react";
import PropTypes from "prop-types";
import { Modal, message } from "antd";
import KycDocViewer from "../credit-checker/components/kyc-doc-viewer";
import UploadDocsModal from "./upload-docs-modal";
// The viewer carries its own `cc-docviewer` styling in the Credit Checker
// sheet. That sheet is already in this bundle (the Credit Checker tab imports
// it from the same tab strip), so this only guarantees it is present.
import "styles/credit-checker.css";
import {
    Card,
    CardHead,
    Icon,
    ICONS,
    Pill,
    SectionHeading,
    Segmented,
    Toggle,
    EmptyState,
    StatusBadge
} from "./ui";
import DocThumb from "./doc-thumb";
import {
    DOC_FILTER_ALL,
    DOC_FILTER_COMPANY,
    DOC_FILTER_PENDING,
    DOC_STATUS,
    DOCS_ARCHIVE_STAGE
} from "./constants";
import { date } from "./data-utils";

/**
 * Documents — the review surface.
 *
 * "Review & tag" pairs the case's file list with a detail pane: retag the
 * document type, move it between company- and applicant-level, see what the
 * parser pulled off the file, and approve it. Every write goes through the
 * same redux actions the Documents Review tab uses, so both tabs stay in sync.
 *
 * "Final categorisation" is the read-only roll-up an operator checks before
 * triggering the kit — what is verified, per owner, and what is still open.
 */

/**
 * The file itself.
 *
 * Reuses the Credit Checker's viewer: it fetches through DocumentService (so
 * the request is authenticated), then renders an image with zoom and rotate, a
 * PDF in an iframe, or a spreadsheet as a table. The old preview pointed an
 * `<img>` straight at FILE_DOWNLOAD_URL, which is behind auth — so nothing
 * ever appeared.
 */
const Preview = ({ doc, onExpand }) => (
    <div className={"ops-ws__preview"}>
        <div className={"ops-ws__preview-head"}>
            <span className={"ops-ws__eyebrow"}>{"Preview"}</span>
            <button type={"button"} className={"ops-ws__link"} onClick={onExpand}>
                <Icon d={ICONS.eye} size={13} />
                {"Open full screen"}
            </button>
        </div>
        <div className={"ops-ws__preview-stage"}>
            <KycDocViewer filePath={doc.fileUrl} />
        </div>
        <div className={"ops-ws__hint"}>
            {"Check the name and identifier against the applicant record before verifying."}
        </div>
    </div>
);

Preview.propTypes = { doc: PropTypes.object.isRequired, onExpand: PropTypes.func };

const ViewerModal = ({ doc, onClose }) => (
    <Modal
        visible={Boolean(doc)}
        destroyOnClose
        className={"ops-ws-modal ops-ws-modal--viewer"}
        wrapClassName={"ops-ws-modal-wrap"}
        width={"min(1100px, 94vw)"}
        title={doc ? `${doc.documentName} · ${doc.name}` : ""}
        footer={null}
        onCancel={onClose}
    >
        {doc ? (
            <div className={"ops-ws__viewer-stage"}>
                <KycDocViewer filePath={doc.fileUrl} />
            </div>
        ) : null}
    </Modal>
);

ViewerModal.propTypes = { doc: PropTypes.object, onClose: PropTypes.func.isRequired };

class FileName extends React.Component {
    state = { editing: false, value: "" };

    start = () => this.setState({ editing: true, value: this.props.doc.name || "" });

    save = () => {
        const value = this.state.value.trim();
        this.setState({ editing: false });
        if (value && value !== this.props.doc.name) this.props.onRename(this.props.doc, value);
    };

    render() {
        const { doc, canEdit, busy } = this.props;
        if (!this.state.editing) {
            return (
                <div className={"ops-ws__filename"}>
                    <span className={"ops-ws__filename-text"}>{doc.name}</span>
                    <button
                        type={"button"}
                        className={"ops-ws__icon-btn"}
                        title={"Rename file"}
                        disabled={!canEdit || busy}
                        onClick={this.start}
                    >
                        <Icon d={ICONS.pen} size={12} />
                    </button>
                </div>
            );
        }
        return (
            <div className={"ops-ws__filename"}>
                <input
                    className={"ops-ws__input"}
                    autoFocus
                    value={this.state.value}
                    onChange={e => this.setState({ value: e.target.value })}
                    onKeyDown={e => {
                        if (e.key === "Enter") this.save();
                        if (e.key === "Escape") this.setState({ editing: false });
                    }}
                />
                <button type={"button"} className={"ops-ws__btn"} onClick={this.save}>
                    {"Save"}
                </button>
                <button
                    type={"button"}
                    className={"ops-ws__link"}
                    onClick={() => this.setState({ editing: false })}
                >
                    {"Cancel"}
                </button>
            </div>
        );
    }
}

FileName.propTypes = {
    doc: PropTypes.object.isRequired,
    canEdit: PropTypes.bool,
    busy: PropTypes.bool,
    onRename: PropTypes.func.isRequired
};

const DocDetail = ({
    doc,
    docTypeOptions,
    applicants,
    canEdit,
    busy,
    onRetag,
    onReassign,
    onApprove,
    onReject,
    onRequest,
    onExpand,
    onRename
}) => {
    const isVerified = Number(doc.status) === DOC_STATUS.VERIFIED;
    return (
        <Card>
            <div className={"ops-ws__doc-detail-head"}>
                <div style={{ minWidth: 0 }}>
                    <FileName
                        doc={doc}
                        canEdit={canEdit}
                        busy={busy}
                        onRename={onRename}
                    />
                    <div style={{ fontSize: 12, color: "#667085", fontWeight: 600, marginTop: 3 }}>
                        {doc.owner}
                    </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flex: "none" }}>
                    <span
                        className={"ops-ws__dot ops-ws__dot--lg"}
                        style={{ background: doc.statusMeta.dot }}
                    />
                    <span style={{ fontSize: 12, fontWeight: 700, color: "#475467" }}>
                        {doc.statusMeta.text}
                    </span>
                </div>
            </div>

            <div className={"ops-ws__doc-detail-body"}>
                <div className={"ops-ws__doc-col"}>
                    <div>
                        <div className={"ops-ws__eyebrow"} style={{ marginBottom: 5 }}>
                            Document type
                        </div>
                        <select
                            className={"ops-ws__select"}
                            style={{ borderLeftWidth: 1 }}
                            value={doc.documentId || ""}
                            disabled={!canEdit || busy}
                            onChange={e => onRetag(doc, Number(e.target.value))}
                        >
                            <option value={""}>Untagged</option>
                            {docTypeOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                marginBottom: 7
                            }}
                        >
                            <div className={"ops-ws__eyebrow"}>Applicant-level document</div>
                            <Toggle
                                on={doc.isApplicantLevel}
                                disabled={!canEdit || busy || !applicants.length}
                                onChange={next =>
                                    onReassign(
                                        doc,
                                        next ? (applicants[0] && applicants[0].id) || null : null
                                    )
                                }
                            />
                        </div>
                        {doc.isApplicantLevel ? (
                            <select
                                className={"ops-ws__select"}
                                style={{ borderLeftWidth: 1 }}
                                value={doc.associateId || ""}
                                disabled={!canEdit || busy}
                                onChange={e => onReassign(doc, Number(e.target.value))}
                            >
                                {applicants.map(applicant => (
                                    <option key={applicant.id} value={applicant.id}>
                                        {applicant.name}
                                    </option>
                                ))}
                            </select>
                        ) : (
                            <div className={"ops-ws__note"}>
                                Filed against the company, not a single applicant.
                            </div>
                        )}
                    </div>

                    <div className={"ops-ws__divider"}>
                        <div className={"ops-ws__eyebrow"} style={{ marginBottom: 9 }}>
                            Extracted from file
                        </div>
                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: "10px 16px"
                            }}
                        >
                            <div>
                                <div className={"ops-ws__kv-label"}>Name on document</div>
                                <div className={"ops-ws__kv-value"}>
                                    {doc.parsedName || "Not parsed"}
                                </div>
                            </div>
                            <div>
                                <div className={"ops-ws__kv-label"}>Identifier</div>
                                <div className={"ops-ws__kv-value"}>
                                    {doc.parsedId || "Not parsed"}
                                </div>
                            </div>
                            <div>
                                <div className={"ops-ws__kv-label"}>Category</div>
                                <div className={"ops-ws__kv-value"}>{doc.category || "—"}</div>
                            </div>
                            <div>
                                <div className={"ops-ws__kv-label"}>Last updated</div>
                                <div className={"ops-ws__kv-value"}>{date(doc.modified)}</div>
                            </div>
                        </div>
                    </div>

                    <button
                        type={"button"}
                        className={
                            "ops-ws__btn ops-ws__btn--block " +
                            (isVerified ? "ops-ws__btn--done" : "ops-ws__btn--primary")
                        }
                        disabled={!canEdit || busy || !doc.documentId}
                        onClick={() => (isVerified ? onReject(doc) : onApprove(doc))}
                        title={
                            doc.documentId
                                ? undefined
                                : "Tag a document type before approving"
                        }
                    >
                        {isVerified ? "Verified ✓ · Move back to pending" : "Approve & mark verified"}
                    </button>

                    {/* Ask the customer for the file again. */}
                    <div className={"ops-ws__doc-actions"}>
                        <button
                            type={"button"}
                            className={"ops-ws__btn"}
                            disabled={!canEdit || busy}
                            onClick={() => onRequest(doc)}
                        >
                            <Icon d={ICONS.mail} size={13} />
                            {"Request again"}
                        </button>
                    </div>
                    {doc.requestRemarks ? (
                        <div className={"ops-ws__note"}>
                            {`Requested from customer: ${doc.requestRemarks}`}
                        </div>
                    ) : null}
                </div>

                <Preview doc={doc} onExpand={() => onExpand(doc)} />
            </div>
        </Card>
    );
};

DocDetail.propTypes = {
    doc: PropTypes.object.isRequired,
    docTypeOptions: PropTypes.array.isRequired,
    applicants: PropTypes.array.isRequired,
    canEdit: PropTypes.bool,
    busy: PropTypes.bool,
    onRetag: PropTypes.func.isRequired,
    onReassign: PropTypes.func.isRequired,
    onApprove: PropTypes.func.isRequired,
    onReject: PropTypes.func.isRequired,
    onRequest: PropTypes.func.isRequired,
    onExpand: PropTypes.func.isRequired,
    onRename: PropTypes.func.isRequired
};

/**
 * Final categorisation — what is filed against whom, as a wall of the
 * documents themselves.
 *
 * The roll-up an operator checks before the kit goes out, so each owner reads
 * as a card with its own completion bar and a tile per document: the file's
 * own picture, what it was tagged as, and where it stands. Clicking a tile
 * opens the full viewer.
 */
const ownerProgress = docs => {
    const verified = docs.filter(d => Number(d.status) === DOC_STATUS.VERIFIED).length;
    return {
        verified,
        total: docs.length,
        percent: docs.length ? Math.round((verified / docs.length) * 100) : 0,
        done: docs.length > 0 && verified === docs.length
    };
};

const DocTile = ({ doc, onOpen }) => (
    <div
        className={`ops-ws__tile is-${doc.statusMeta.key}`}
        role={"button"}
        tabIndex={0}
        onClick={() => onOpen(doc)}
        onKeyDown={e => {
            if (e.key === "Enter" || e.key === " ") onOpen(doc);
        }}
    >
        <DocThumb doc={doc} size={56} />
        <div className={"ops-ws__tile-body"}>
            <div className={"ops-ws__tile-name"}>{doc.documentName}</div>
            <div className={"ops-ws__tile-file"}>{doc.name}</div>
            <Pill tone={doc.statusMeta.tone}>{doc.statusMeta.label}</Pill>
        </div>
    </div>
);

DocTile.propTypes = { doc: PropTypes.object.isRequired, onOpen: PropTypes.func.isRequired };

const FinalView = ({ documents, applicants, companyName, readiness, onOpen }) => {
    const company = documents.filter(d => !d.associateId);
    const groups = [
        {
            key: "company",
            title: companyName || "Company",
            sub: "Company documents",
            icon: ICONS.bank,
            items: company
        },
        ...applicants.map(applicant => ({
            key: applicant.id,
            title: applicant.name,
            sub: applicant.typeLabel,
            icon: ICONS.person,
            items: documents.filter(d => d.associateId === applicant.id)
        }))
    ];

    const overall = ownerProgress(documents);

    return (
        <div className={"ops-ws__grid ops-ws__grid--wide"}>
            <div className={"ops-ws__stack"}>
                <Card>
                    <div className={"ops-ws__final-head"}>
                        <div>
                            <div className={"ops-ws__card-head-title"}>
                                {"Final categorisation"}
                            </div>
                            <div className={"ops-ws__card-head-sub"}>
                                {"Every file on the case, filed against its owner."}
                            </div>
                        </div>
                        <div className={"ops-ws__final-score"}>
                            <span className={"ops-ws__final-score-value"}>
                                {`${overall.verified}/${overall.total}`}
                            </span>
                            <span className={"ops-ws__final-score-label"}>{"verified"}</span>
                        </div>
                    </div>
                    <div className={"ops-ws__meter"}>
                        <span
                            className={
                                "ops-ws__meter-fill" + (overall.done ? " is-done" : "")
                            }
                            style={{ width: `${overall.percent}%` }}
                        />
                    </div>
                </Card>

                {groups.map(group => {
                    const progress = ownerProgress(group.items);
                    return (
                        <Card key={group.key}>
                            <div className={"ops-ws__owner-head"}>
                                <span className={"ops-ws__group-icon"}>
                                    <Icon d={group.icon} size={16} />
                                </span>
                                <div className={"ops-ws__owner-titles"}>
                                    <div className={"ops-ws__owner-name"}>{group.title}</div>
                                    <div className={"ops-ws__owner-sub"}>{group.sub}</div>
                                </div>
                                <Pill tone={progress.done ? "ok" : "warn"}>
                                    {`${progress.verified} / ${progress.total} verified`}
                                </Pill>
                            </div>
                            <div className={"ops-ws__meter ops-ws__meter--thin"}>
                                <span
                                    className={
                                        "ops-ws__meter-fill" +
                                        (progress.done ? " is-done" : "")
                                    }
                                    style={{ width: `${progress.percent}%` }}
                                />
                            </div>
                            {group.items.length ? (
                                <div className={"ops-ws__tiles"}>
                                    {group.items.map(item => (
                                        <DocTile key={item.id} doc={item} onOpen={onOpen} />
                                    ))}
                                </div>
                            ) : (
                                <div className={"ops-ws__card-body"}>
                                    <div className={"ops-ws__note"}>
                                        {"Nothing filed against this owner yet."}
                                    </div>
                                </div>
                            )}
                        </Card>
                    );
                })}
            </div>

            <div className={"ops-ws__stack"}>
                <Card>
                    <CardHead title={"Readiness"} sub={"What the PS Kit gate checks."} />
                    <div className={"ops-ws__card-body ops-ws__kv-list"}>
                        {readiness.map(row => (
                            <div key={row.label} className={"ops-ws__check-row"}>
                                <StatusBadge tone={row.ok ? "ok" : "warn"} small />
                                <span style={{ flex: 1, minWidth: 0 }}>{row.label}</span>
                                <Pill tone={row.ok ? "ok" : "warn"}>{row.value}</Pill>
                            </div>
                        ))}
                    </div>
                </Card>
            </div>
        </div>
    );
};

FinalView.propTypes = {
    documents: PropTypes.array.isRequired,
    applicants: PropTypes.array.isRequired,
    companyName: PropTypes.string,
    readiness: PropTypes.array.isRequired,
    onOpen: PropTypes.func.isRequired
};

/**
 * A plain list of files — what the requested and archive views show. Neither
 * is a review queue: one is waiting on the customer, the other is off the case
 * until somebody restores it.
 */
const FileList = ({ title, note, docs, onOpen, picked, onPick, action, empty }) => (
    <Card>
        <CardHead title={title} sub={note} extra={action} />
        {docs.length ? (
            <div className={"ops-ws__tiles ops-ws__tiles--pad"}>
                {docs.map(doc => (
                    <div key={doc.id} className={"ops-ws__tile-wrap"}>
                        {picked ? (
                            <input
                                type={"checkbox"}
                                className={"ops-ws__checkbox ops-ws__tile-check"}
                                checked={picked.includes(doc.id)}
                                onChange={() => onPick(doc.id)}
                            />
                        ) : null}
                        <DocTile doc={doc} onOpen={onOpen} />
                        {doc.requestRemarks ? (
                            <div className={"ops-ws__tile-note"}>{doc.requestRemarks}</div>
                        ) : null}
                    </div>
                ))}
            </div>
        ) : (
            <div className={"ops-ws__card-body"}>
                <EmptyState>{empty}</EmptyState>
            </div>
        )}
    </Card>
);

FileList.defaultProps = { picked: null, onPick: () => {} };

FileList.propTypes = {
    title: PropTypes.string.isRequired,
    note: PropTypes.string,
    docs: PropTypes.array.isRequired,
    onOpen: PropTypes.func.isRequired,
    picked: PropTypes.array,
    onPick: PropTypes.func,
    action: PropTypes.node,
    empty: PropTypes.string.isRequired
};

class DocumentsSection extends React.Component {
    state = { viewing: null, requesting: null, uploading: false, uploadOpen: false, picked: [], query: "" };

    /** Search across file name, document type and owner. */
    matchesQuery = doc => {
        const q = this.state.query.trim().toLowerCase();
        if (!q) return true;
        return [doc.name, doc.documentName, doc.owner].some(
            value => value && String(value).toLowerCase().indexOf(q) >= 0
        );
    };

    togglePick = id =>
        this.setState(({ picked }) => ({
            picked: picked.includes(id)
                ? picked.filter(value => value !== id)
                : picked.concat(id)
        }));

    runArchive = action => {
        const picked = this.state.picked;
        if (!picked.length) return;
        Promise.resolve(this.props.onArchive(picked, action)).then(() =>
            this.setState({ picked: [] })
        );
    };

    openViewer = doc => this.setState({ viewing: doc });

    closeViewer = () => this.setState({ viewing: null });

    openRequest = doc =>
        this.setState({
            requesting: { doc, remarks: doc.requestRemarks || "", saving: false }
        });

    submitRequest = () => {
        const { doc, remarks } = this.state.requesting;
        if (!remarks || remarks.trim().length < 5) {
            message.error("Say what is wrong with the document — at least 5 characters.");
            return;
        }
        this.setState({ requesting: { ...this.state.requesting, saving: true } });
        Promise.resolve(this.props.onRequest(doc, remarks.trim())).then(() =>
            this.setState({ requesting: null })
        );
    };

    openUpload = () => this.setState({ uploadOpen: true });

    closeUpload = () => {
        if (!this.state.uploading) this.setState({ uploadOpen: false });
    };

    /**
     * Document Review's upload rules: the operator names the document type
     * (and the applicant, for applicant-level types) before the files go up,
     * so nothing lands untagged.
     */
    submitUpload = values => {
        this.setState({ uploading: true });
        this.props.onUpload(values, ok =>
            this.setState({ uploading: false, uploadOpen: ok ? false : this.state.uploadOpen })
        );
    };

    renderUploadModal() {
        const { applicants, documents } = this.props;
        return (
            <UploadDocsModal
                open={this.state.uploadOpen}
                onClose={this.closeUpload}
                onSubmit={this.submitUpload}
                applicants={applicants}
                existingNames={documents.map(doc => doc.name)}
                busy={this.state.uploading}
                disabled={!this.props.canEdit}
            />
        );
    }

    render() {
        const {
            documents,
            applicants,
            companyName,
            docTypeOptions,
            view,
            onViewChange,
            filter,
            onFilterChange,
            selectedId,
            onSelect,
            canEdit,
            busy,
            readiness,
            onRetag,
            onReassign,
            onApprove,
            onReject,
            onNotify,
            onRequestNew
        } = this.props;

        // Requested and archived files have their own views, as they do on the
        // Document Review list — the review queue is what is actually on file.
        const live = documents.filter(
            doc =>
                ![DOC_STATUS.REQUESTED, DOC_STATUS.ARCHIVED].includes(Number(doc.status))
        );
        const requested = documents.filter(
            doc => Number(doc.status) === DOC_STATUS.REQUESTED
        );
        const archived = documents.filter(
            doc => Number(doc.status) === DOC_STATUS.ARCHIVED
        );
        const searching = this.state.query.trim() !== "";
        const searchBox = (
            <div className={"ops-ws__doc-search"}>
                <Icon d={ICONS.search} size={14} />
                <input
                    className={"ops-ws__doc-search-input"}
                    placeholder={"Search by file, document type or applicant"}
                    value={this.state.query}
                    onChange={e => this.setState({ query: e.target.value })}
                />
                {searching ? (
                    <button
                        type={"button"}
                        className={"ops-ws__doc-search-clear"}
                        onClick={() => this.setState({ query: "" })}
                        aria-label={"Clear search"}
                    >
                        <Icon d={ICONS.cross} size={12} width={2.2} />
                    </button>
                ) : null}
            </div>
        );
        const canArchive =
            canEdit && Number(this.props.stageId) === DOCS_ARCHIVE_STAGE;

        const filters = [
            { value: DOC_FILTER_ALL, label: "All" },
            { value: DOC_FILTER_COMPANY, label: "Company" },
            ...applicants.map(applicant => ({
                value: String(applicant.id),
                label: applicant.name.split(" ")[0]
            })),
            { value: DOC_FILTER_PENDING, label: "Pending" }
        ];

        let visible = live;
        if (filter === DOC_FILTER_COMPANY) {
            visible = live.filter(doc => !doc.associateId);
        } else if (filter === DOC_FILTER_PENDING) {
            visible = live.filter(doc => Number(doc.status) !== DOC_STATUS.VERIFIED);
        } else if (filter !== DOC_FILTER_ALL) {
            visible = live.filter(doc => String(doc.associateId) === filter);
        }
        visible = visible.filter(this.matchesQuery);

        const selected =
            live.find(doc => doc.id === selectedId) || visible[0] || live[0];

        return (
            <div className={"ops-ws__section"}>
                <SectionHeading
                    title={"Documents"}
                    subtitle={
                        "Review each file, tag its type and owner, then finalise the categorisation."
                    }
                    extra={
                        <div className={"ops-ws__head-actions"}>
                            <button
                                type={"button"}
                                className={"ops-ws__btn"}
                                disabled={!canEdit || this.state.uploading}
                                onClick={this.openUpload}
                            >
                                <Icon d={ICONS.download} size={13} />
                                {this.state.uploading ? "Uploading…" : "Upload"}
                            </button>
                            {onRequestNew ? (
                                <button
                                    type={"button"}
                                    className={"ops-ws__btn"}
                                    disabled={!canEdit}
                                    onClick={onRequestNew}
                                >
                                    <Icon d={ICONS.file} size={13} />
                                    {"Request document"}
                                </button>
                            ) : null}
                            <button
                                type={"button"}
                                className={"ops-ws__btn"}
                                disabled={!canEdit}
                                onClick={onNotify}
                            >
                                <Icon d={ICONS.mail} size={13} />
                                {"Notify customer"}
                            </button>
                            <Segmented
                                value={view}
                                onChange={onViewChange}
                                options={[
                                    { value: "review", label: "Review & tag" },
                                    { value: "final", label: "Final categorisation" },
                                    {
                                        value: "requested",
                                        label: requested.length
                                            ? `Requested (${requested.length})`
                                            : "Requested"
                                    },
                                    {
                                        value: "archived",
                                        label: archived.length
                                            ? `Archive (${archived.length})`
                                            : "Archive"
                                    }
                                ]}
                            />
                        </div>
                    }
                />

                {view === "requested" || view === "archived" ? (
                    <div style={{ maxWidth: 420, marginTop: 4 }}>{searchBox}</div>
                ) : null}

                {view === "requested" ? (
                    <FileList
                        title={"Requested from the customer"}
                        note={
                            "Documents the customer has been asked for and has not sent back yet."
                        }
                        docs={requested.filter(this.matchesQuery)}
                        onOpen={this.openViewer}
                        empty={"Nothing is outstanding with the customer."}
                    />
                ) : view === "archived" ? (
                    <FileList
                        title={"Archive"}
                        note={
                            canArchive
                                ? "Files taken off the case. Restore one to put it back in the review queue."
                                : "Files taken off the case. Restoring is only available on the ops stage."
                        }
                        docs={archived.filter(this.matchesQuery)}
                        onOpen={this.openViewer}
                        picked={canArchive ? this.state.picked : null}
                        onPick={this.togglePick}
                        action={
                            canArchive ? (
                                <button
                                    type={"button"}
                                    className={"ops-ws__btn ops-ws__btn--primary"}
                                    disabled={!this.state.picked.length}
                                    onClick={() => this.runArchive("restore")}
                                >
                                    <Icon d={ICONS.refresh} size={13} />
                                    {`Restore${
                                        this.state.picked.length
                                            ? ` (${this.state.picked.length})`
                                            : ""
                                    }`}
                                </button>
                            ) : null
                        }
                        empty={"Nothing has been archived off this case."}
                    />
                ) : !documents.length ? (
                    <EmptyState>No documents uploaded against this case yet.</EmptyState>
                ) : view === "final" ? (
                    <FinalView
                        documents={documents}
                        applicants={applicants}
                        companyName={companyName}
                        readiness={readiness}
                        onOpen={this.openViewer}
                    />
                ) : (
                    <div
                        className={"ops-ws__grid"}
                        style={{ gridTemplateColumns: "minmax(320px, 400px) 1fr" }}
                    >
                        <Card className={"ops-ws__card-pad"}>
                            {canArchive ? (
                                <div className={"ops-ws__list-actions"}>
                                    <span className={"ops-ws__hint"}>
                                        {this.state.picked.length
                                            ? `${this.state.picked.length} selected`
                                            : "Tick files to archive them off the case."}
                                    </span>
                                    <button
                                        type={"button"}
                                        className={"ops-ws__btn"}
                                        disabled={!this.state.picked.length}
                                        onClick={() => this.runArchive("archive")}
                                    >
                                        {"Archive selected"}
                                    </button>
                                </div>
                            ) : null}
                            {searchBox}
                            <div className={"ops-ws__filters"}>
                                {filters.map(item => (
                                    <button
                                        key={item.value}
                                        type={"button"}
                                        className={filter === item.value ? "is-active" : ""}
                                        onClick={() => onFilterChange(item.value)}
                                    >
                                        {item.label}
                                    </button>
                                ))}
                            </div>
                            <div className={"ops-ws__doc-scroll"}>
                                {visible.length ? (
                                    visible.map(doc => (
                                        <div
                                            key={doc.id}
                                            className={
                                                "ops-ws__doc-row" +
                                                (selected && selected.id === doc.id
                                                    ? " is-active"
                                                    : "")
                                            }
                                            role={"button"}
                                            tabIndex={0}
                                            onClick={() => onSelect(doc.id)}
                                            onKeyDown={e => {
                                                if (e.key === "Enter") onSelect(doc.id);
                                            }}
                                        >
                                            {canArchive ? (
                                                <input
                                                    type={"checkbox"}
                                                    className={"ops-ws__checkbox"}
                                                    checked={this.state.picked.includes(doc.id)}
                                                    onClick={e => e.stopPropagation()}
                                                    onChange={() => this.togglePick(doc.id)}
                                                />
                                            ) : null}
                                            <DocThumb doc={doc} size={36} />
                                            <span style={{ flex: 1, minWidth: 0 }}>
                                                <span
                                                    className={"ops-ws__doc-name"}
                                                    style={{ display: "block" }}
                                                >
                                                    {doc.name}
                                                </span>
                                                <span
                                                    className={"ops-ws__doc-owner"}
                                                    style={{ display: "block" }}
                                                >
                                                    {doc.owner}
                                                </span>
                                            </span>
                                            <Pill tone={doc.statusMeta.tone}>
                                                {doc.statusMeta.label}
                                            </Pill>
                                        </div>
                                    ))
                                ) : (
                                    <div style={{ padding: 16, color: "#98a2b3", fontSize: 12.5 }}>
                                        {searching
                                            ? `No documents match "${this.state.query.trim()}".`
                                            : "No documents match this filter."}
                                    </div>
                                )}
                            </div>
                        </Card>

                        {selected ? (
                            <DocDetail
                                doc={selected}
                                docTypeOptions={docTypeOptions}
                                applicants={applicants}
                                canEdit={canEdit}
                                busy={busy}
                                onRetag={onRetag}
                                onReassign={onReassign}
                                onApprove={onApprove}
                                onReject={onReject}
                                onRequest={this.openRequest}
                                onExpand={this.openViewer}
                                onRename={this.props.onRename}
                            />
                        ) : null}
                    </div>
                )}

                <ViewerModal doc={this.state.viewing} onClose={this.closeViewer} />

                <Modal
                    visible={Boolean(this.state.requesting)}
                    destroyOnClose
                    className={"ops-ws-modal"}
                    wrapClassName={"ops-ws-modal-wrap"}
                    width={480}
                    title={"Request this document again"}
                    okText={"Send request"}
                    confirmLoading={Boolean(
                        this.state.requesting && this.state.requesting.saving
                    )}
                    onOk={this.submitRequest}
                    onCancel={() => this.setState({ requesting: null })}
                >
                    {this.state.requesting ? (
                        <React.Fragment>
                            <div className={"ops-ws__field-label"}>
                                {"What is wrong with it?"}
                            </div>
                            <textarea
                                className={"ops-ws__textarea"}
                                placeholder={"Blurred, cropped, expired…"}
                                value={this.state.requesting.remarks}
                                onChange={e =>
                                    this.setState({
                                        requesting: {
                                            ...this.state.requesting,
                                            remarks: e.target.value
                                        }
                                    })
                                }
                            />
                            <div className={"ops-ws__hint"} style={{ marginTop: 10 }}>
                                {"The customer sees this on the document request; use Notify customer to email the outstanding list."}
                            </div>
                        </React.Fragment>
                    ) : null}
                </Modal>

                {this.renderUploadModal()}
            </div>
        );
    }
}

DocumentsSection.propTypes = {
    documents: PropTypes.array.isRequired,
    applicants: PropTypes.array.isRequired,
    companyName: PropTypes.string,
    docTypeOptions: PropTypes.array.isRequired,
    view: PropTypes.string.isRequired,
    onViewChange: PropTypes.func.isRequired,
    filter: PropTypes.string.isRequired,
    onFilterChange: PropTypes.func.isRequired,
    selectedId: PropTypes.number,
    onSelect: PropTypes.func.isRequired,
    canEdit: PropTypes.bool,
    busy: PropTypes.bool,
    readiness: PropTypes.array.isRequired,
    onRetag: PropTypes.func.isRequired,
    onReassign: PropTypes.func.isRequired,
    onApprove: PropTypes.func.isRequired,
    onReject: PropTypes.func.isRequired,
    onRequest: PropTypes.func.isRequired,
    onRequestNew: PropTypes.func,
    onUpload: PropTypes.func.isRequired,
    onNotify: PropTypes.func.isRequired,
    onRename: PropTypes.func.isRequired,
    onArchive: PropTypes.func.isRequired,
    stageId: PropTypes.number
};

export default DocumentsSection;
